import { useEffect, useState } from 'react';
import styles from './Countdown.module.css';

type CountdownProps = {
  countdownTo: number;
  type: 'normal' | 'large';
  clean?: boolean;
};

function padTime(time: number) {
  return time >= 10 ? String(time) : `0${time}`;
}

export function Countdown({ countdownTo, type, clean = false }: CountdownProps) {
  const [now, setNow] = useState<number | null>(null);
  const intervalParts = getIntervalParts(countdownTo, now);

  let intervalTime = 60000;
  if (intervalParts !== null && intervalParts.sec !== null) {
    intervalTime = 1000;
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: setNow is a state setter and doesn't need to be in deps
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date().getTime());
    }, intervalTime);
    setNow(new Date().getTime());
    return () => clearInterval(interval);
  }, [setNow, intervalTime]);

  if (intervalParts === null) {
    return null;
  }

  const { days, hours, minutes, sec } = intervalParts;

  return (
    <div
      className={`${clean ? styles.cleanBaseContainer : styles.baseContainer} ${
        type === 'large' ? styles.large : styles.normal
      }`}
    >
      {days !== null && (
        <h3 className={styles.time}>
          {padTime(days)}
          <div className={styles.unit}>dage</div>
        </h3>
      )}
      <h3 className={styles.time}>
        {padTime(hours)}
        <div className={styles.unit}>timer</div>
      </h3>
      <h3 className={styles.time}>
        {padTime(minutes)}
        <div className={styles.unit}>minutter</div>
      </h3>
      {sec !== null && (
        <h3 className={styles.time}>
          {padTime(sec)}
          <div className={styles.unit}>sek</div>
        </h3>
      )}
    </div>
  );
}

function getIntervalParts(
  starts: number,
  now: number | null
): { days: number | null; hours: number; minutes: number; sec: number | null } | null {
  if (now === null) {
    return null;
  }

  let milliseconds = starts * 1000 - now;

  if (milliseconds < 0) {
    return { days: null, hours: 0, minutes: 0, sec: 0 };
  }

  const days = Math.floor(milliseconds / (24 * 3600 * 1000));
  milliseconds -= days * 24 * 3600 * 1000;

  const hours = Math.floor(milliseconds / (3600 * 1000));
  milliseconds -= hours * 3600 * 1000;

  const minutes = Math.floor(milliseconds / (60 * 1000));
  milliseconds -= minutes * 60 * 1000;

  const sec = Math.floor(milliseconds / 1000);

  return { days: days > 0 ? days : null, hours, minutes, sec: days === 0 ? sec : null };
}
